import React, { useState, useEffect, useCallback } from 'react';
import { FaArrowRight, FaArrowLeft, FaTimes, FaDesktop } from 'react-icons/fa';
import { useOverlay } from '../../contexts/FocusContext';
import './OnboardingTour.css'; // Reuse the same styles

export const SCREEN_SHARE_TUTORIAL_STORAGE_KEY = 'screenshare_tutorial_completed';

interface TourStep {
  id: string;
  targetId: string | null; // null for no spotlight (welcome/finish steps)
  title: string;
  description: string;
}

const tourSteps: TourStep[] = [
  {
    id: 'welcome',
    targetId: null,
    title: 'Screen Sharing Enabled!',
    description: 'You now have permission to share your screen with everyone in the room.',
  },
  {
    id: 'quality-selector',
    targetId: 'tour-screenshare-quality',
    title: 'Choose Quality',
    description: 'Select your preferred quality. Higher quality looks better but uses more bandwidth. Lower settings work better on slower connections.',
  },
  {
    id: 'share-button',
    targetId: 'tour-screenshare-button',
    title: 'Start Sharing',
    description: 'Click this button to start sharing your screen. You can share a specific window or your entire screen.',
  },
  {
    id: 'presentation-mode',
    targetId: 'tour-presentation-mode',
    title: 'Presentation Mode',
    description: 'Enable this to reduce graphics quality, giving more resources to your screen share for smoother streaming.',
  },
  {
    id: 'finish',
    targetId: null,
    title: "You're Ready to Share!",
    description: 'Select your quality, click Share Screen, and present to everyone. You can stop sharing anytime by clicking the button again.',
  },
];

interface ScreenShareTourProps {
  isVisible: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

interface SpotlightPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

const ScreenShareTour: React.FC<ScreenShareTourProps> = ({ isVisible, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightPosition, setSpotlightPosition] = useState<SpotlightPosition | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const step = tourSteps[currentStep];

  // Register as overlay when visible - FocusContext handles Unity capture
  useOverlay('screenshare-tour', isVisible);

  // Update spotlight position only (tooltip stays centered)
  const updateSpotlight = useCallback(() => {
    if (!step || !step.targetId) {
      setSpotlightPosition(null);
      return;
    }

    const targetElement = document.querySelector(`[data-tour-id="${step.targetId}"]`);
    if (!targetElement) {
      setSpotlightPosition(null);
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    const padding = 8;

    setSpotlightPosition({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });
  }, [step]);

  // Update spotlight when step changes or window resizes
  useEffect(() => {
    if (!isVisible) return;

    updateSpotlight();
    const raf = requestAnimationFrame(() => updateSpotlight());
    const retry = window.setTimeout(() => updateSpotlight(), 120);

    const handleResize = () => updateSpotlight();
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(retry);
      window.removeEventListener('resize', handleResize);
    };
  }, [isVisible, currentStep, updateSpotlight]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handleBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, currentStep]);

  const handleNext = () => {
    if (isAnimating) return;

    if (currentStep < tourSteps.length - 1) {
      setIsAnimating(true);
      setCurrentStep(currentStep + 1);
      setTimeout(() => setIsAnimating(false), 300);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (isAnimating) return;

    if (currentStep > 0) {
      setIsAnimating(true);
      setCurrentStep(currentStep - 1);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const handleComplete = () => {
    markScreenShareTutorialCompleted();
    setCurrentStep(0);
    onComplete();
  };

  const handleSkip = () => {
    markScreenShareTutorialCompleted();
    setCurrentStep(0);
    onSkip();
  };

  if (!isVisible) return null;

  const isLastStep = currentStep === tourSteps.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div className="onboarding-tour-overlay" style={{ zIndex: 100000 }}>
      {/* Dark overlay with spotlight cutout */}
      <div className="onboarding-backdrop">
        {spotlightPosition && (
          <div
            className="onboarding-spotlight"
            style={{
              top: spotlightPosition.top,
              left: spotlightPosition.left,
              width: spotlightPosition.width,
              height: spotlightPosition.height,
            }}
          />
        )}
      </div>

      {/* Tooltip card - always centered */}
      <div className="onboarding-tooltip-container">
        <div className="onboarding-tooltip">
          {/* Skip button */}
          <button
            className="onboarding-skip-button"
            onClick={handleSkip}
            onMouseDown={(e) => e.preventDefault()}
            title="Skip tutorial"
          >
            <FaTimes size={14} />
          </button>

          {/* Content */}
          <h3 className="onboarding-title">{step.title}</h3>
          <p className="onboarding-description">{step.description}</p>

          {/* Navigation */}
          <div className="onboarding-navigation">
            <button
              className="onboarding-nav-button onboarding-back-button"
              onClick={handleBack}
              onMouseDown={(e) => e.preventDefault()}
              disabled={isFirstStep}
              style={{ visibility: isFirstStep ? 'hidden' : 'visible' }}
            >
              <FaArrowLeft size={12} />
              Back
            </button>

            {/* Step indicators */}
            <div className="onboarding-dots">
              {tourSteps.map((_, index) => (
                <span
                  key={index}
                  className={`onboarding-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                />
              ))}
            </div>

            <button
              className="onboarding-nav-button onboarding-next-button"
              onClick={handleNext}
              onMouseDown={(e) => e.preventDefault()}
            >
              {isLastStep ? 'Finish' : 'Next'}
              {!isLastStep && <FaArrowRight size={12} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Persist completion so first-time tour does not block sharing again. */
export const markScreenShareTutorialCompleted = (): void => {
  try {
    localStorage.setItem(SCREEN_SHARE_TUTORIAL_STORAGE_KEY, 'true');
  } catch {
    /* private mode / quota */
  }
};

// Helper function to check if screen share tutorial has been completed
export const hasCompletedScreenShareTutorial = (): boolean => {
  return localStorage.getItem(SCREEN_SHARE_TUTORIAL_STORAGE_KEY) === 'true';
};

// Helper function to reset screen share tutorial (for replay)
export const resetScreenShareTutorial = (): void => {
  localStorage.removeItem(SCREEN_SHARE_TUTORIAL_STORAGE_KEY);
};

export default ScreenShareTour;

